import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ChartData, ChartOptions } from 'chart.js';
import { format } from 'date-fns';
import { abTestAnalyticsApiService, ExperimentAnalytics, ExperimentStatus } from 'src/services';
import {
  AvailableIcons,
  Button,
  Icon,
  Typography,
  TypographyType,
  TypographyVariant,
} from 'src/components-dummy';
import GenericChart from 'src/components-dummy/Chart/Chart';
import { experimentRoutes } from 'src/app-routes';
import {
  formatCompactNumber,
  formatCurrency,
  formatPercent,
  formatSignedPercent,
  getDeltaClassName,
} from 'src/utils';
import { Dispatch } from '../types';
import { experimentActionButtonsActionMethods } from '../Experiment/ExperimentActionButtons/Actions';
import { LiftCell } from '../Experiment/LiftCell';
import { AbTestDashboardProps } from './AbTestDashboard.types';
import { StopExperimentModal } from './StopExperimentModal';
import { ApplyWinnerModal } from './ApplyWinnerModal';
import './AbTestDashboard.scss';

function formatLiftSigned(value: number): JSX.Element {
  const text = formatSignedPercent(value);
  return (
    <span className={getDeltaClassName(value, 'delta-positive', 'delta-negative')}>{text}</span>
  );
}

/**
 * Finds the first non-USD, non-null currency among the variants, so the secondary-metrics
 * table can note that the USD figures shown are converted from the shop's native currency.
 * Returns undefined when every variant is USD (or has no currency yet), in which case no
 * conversion note should be shown.
 */
export function findSourceCurrency(
  variants: Array<{ currency?: string | null }> | undefined
): string | undefined {
  return variants?.find(v => v.currency && v.currency !== 'USD')?.currency ?? undefined;
}

export const AbTestDashboard = ({
  shopId,
  experimentId,
  experiment,
  onBack,
}: AbTestDashboardProps): JSX.Element => {
  const dispatch = useDispatch() as unknown as Dispatch;
  const history = useHistory();
  const [analytics, setAnalytics] = useState<ExperimentAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [status, setStatus] = useState(experiment.status);
  const [isStopping, setIsStopping] = useState(false);

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    setError(null);
    return abTestAnalyticsApiService
      .getExperimentAnalytics({ shopId, experimentId })
      .then(data => {
        setAnalytics(data);
      })
      .catch(() => {
        setError('Failed to load analytics data. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [shopId, experimentId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const onStopExperiment = async (): Promise<void> => {
    setShowStopDialog(false);
    setIsStopping(true);
    try {
      const updatedExperiment = await experimentActionButtonsActionMethods.stopExperiment({
        shopId,
        id: experiment.id,
        experiment,
      })(dispatch);
      if (updatedExperiment) {
        setStatus(updatedExperiment.status);
        fetchAnalytics();
      }
    } finally {
      setIsStopping(false);
    }
  };

  const onViewConfig = (): void => {
    history.push(
      generatePath(experimentRoutes.editExperiment.path, { shopId, experimentId: experiment.id })
    );
  };

  const applyGate = useMemo(() => {
    if (!analytics || analytics.state !== 'winner_found') return null;
    const winnerVariant = analytics.variants.find(v => v.isWinner);
    if (!winnerVariant || winnerVariant.variantType === 'control') return null;
    return { variantId: winnerVariant.experienceId, variantName: winnerVariant.variantName };
  }, [analytics]);

  const onApplied = (updatedExperiment: { status: ExperimentStatus }): void => {
    setShowApplyDialog(false);
    setStatus(updatedExperiment.status);
    fetchAnalytics();
  };

  const METRIC_LABELS: Record<string, string> = {
    user_conversion: 'User CVR',
    user_cvr: 'User CVR',
    cvr: 'User CVR',
    conversion_rate: 'User CVR',
    purchase_rate: 'User CVR',
    average_revenue_per_user: 'ARPU',
    arpu: 'ARPU',
    aov: 'AOV',
    average_order_value: 'AOV',
    revenue: 'Revenue',
  };

  const heroVerdict = (() => {
    if (!analytics) return null;
    const { state, daysInTest, variants = [] } = analytics;
    const totalUsers = variants.reduce((sum, v) => sum + v.users, 0);

    // The backend already knows who won (it accounts for control winning too) — trust it
    // instead of re-deriving "the winner" by excluding control and ranking by probability.
    const winnerVariant = variants.find(v => v.isWinner);
    const winnerIsControl = winnerVariant?.variantType === 'control';
    // The strongest test-variant challenger, used to size/word the verdict when control wins
    // (control's own probabilityOfWinning/lift are always reported as 0 against itself).
    const challenger = variants
      .filter(v => v.variantType !== 'control')
      .sort((a, b) => b.probabilityOfWinning - a.probabilityOfWinning)[0];

    const winnerName = winnerVariant?.variantName ?? 'Control';
    const opponentName = winnerIsControl
      ? challenger?.variantName ?? 'the test variant'
      : 'Control';
    const winnerProb = winnerIsControl
      ? 1 - (challenger?.probabilityOfWinning ?? 0)
      : winnerVariant?.probabilityOfWinning ?? 0;
    // Lift is always expressed as "how much better is the winner than the opponent" — never negative.
    const winnerLift = Math.abs(
      (winnerIsControl ? challenger?.lift?.cvr : winnerVariant?.lift?.cvr) ?? analytics.deltas.cvr
    );

    const metricLabel =
      METRIC_LABELS[analytics.primaryMetric.toLowerCase()] ?? analytics.primaryMetric;

    switch (state) {
      case 'collecting_data': {
        const { estimatorInputs } = experiment;
        const waitCopy = estimatorInputs
          ? `You agreed to detect a ${estimatorInputs.mde}% change — that takes about ${estimatorInputs.estimatedDays} days at ${estimatorInputs.dailyTestUsers.toLocaleString()} test users/day.`
          : 'Most tests need ~7 days and a few hundred conversions per variant before a verdict is possible.';
        return {
          icon: '⏳',
          title: 'Collecting data',
          description: `Day ${daysInTest} of test, ${totalUsers.toLocaleString()} users so far. ${waitCopy}`,
        };
      }
      case 'winner_found': {
        const prob = formatPercent(winnerProb, 0);
        const lift = formatPercent(winnerLift);
        const rollout = winnerIsControl
          ? `Safe to stop the test and keep ${winnerName}'s config.`
          : `Safe to stop the test and roll out ${winnerName}'s config.`;
        return {
          icon: '🏆',
          title: `${winnerName} is winning`,
          description: `Day ${daysInTest} · ${totalUsers.toLocaleString()} users · ${prob} probability ${winnerName} beats ${opponentName} on ${metricLabel} (+${lift} lift). ${rollout}`,
        };
      }
      case 'inconclusive':
        return {
          icon: '≡',
          title: 'No clear winner yet',
          description: `Day ${daysInTest} · ${totalUsers.toLocaleString()} users. Not enough data yet for a verdict — keep the test running.`,
        };
      case 'ended_winner': {
        const lift = formatPercent(winnerLift);
        const rollout = winnerIsControl
          ? `No changes needed — keep ${winnerName}'s config.`
          : `Roll out ${winnerName}'s config to capture the gain.`;
        return {
          icon: '🏆',
          title: `${winnerName} won`,
          description: `Ran ${daysInTest} days, ${formatCompactNumber(totalUsers)} users. ${winnerName} beat ${opponentName} by +${lift} on ${metricLabel}. ${rollout}`,
        };
      }
      case 'ended_inconclusive': {
        const lift = formatSignedPercent(analytics.deltas.cvr);
        return {
          icon: '🏁',
          title: 'No clear winner',
          description: `Ran ${daysInTest} days, ${formatCompactNumber(totalUsers)} users. Final lift was ${lift} on ${metricLabel} — too uncertain to call. Treat the variants as equivalent.`,
        };
      }
      default:
        return null;
    }
  })();

  const endedWinnerVariant =
    analytics?.state === 'ended_winner' ? analytics.variants?.find(v => v.isWinner) : null;
  const winnerVariantName = endedWinnerVariant?.variantName ?? null;
  const winnerVariantIsControl = endedWinnerVariant?.variantType === 'control';

  const DEVICE_ICONS: Record<string, string> = {
    desktop: '🖥️',
    mobile: '📱',
    tablet: '📟',
  };

  const TEST_VARIANT_COLORS = ['#1565c0', '#c62828', '#f57c00', '#6a1b9a'];
  const CONTROL_COLOR = '#9e9e9e';

  const primaryMetricLabel = analytics
    ? METRIC_LABELS[analytics.primaryMetric.toLowerCase()] ?? analytics.primaryMetric
    : '';

  const allDates = useMemo<string[]>(() => {
    if (!analytics?.timeSeries?.length) return [];
    return Array.from(new Set(analytics.timeSeries.flatMap(ts => ts.data.map(d => d.date)))).sort();
  }, [analytics]);

  const lineChartData = useMemo<ChartData<'line'> | null>(() => {
    if (!analytics?.timeSeries?.length || !allDates.length) return null;

    let testColorIndex = 0;
    const datasets = analytics.timeSeries.map(ts => {
      const variant = analytics.variants.find(v => v.experienceId === ts.experienceId);
      const isControl = variant?.variantType === 'control';
      const color = isControl
        ? CONTROL_COLOR
        : TEST_VARIANT_COLORS[testColorIndex++ % TEST_VARIANT_COLORS.length];
      const dateMap = new Map(ts.data.map(d => [d.date, d.value]));
      // hasData[i] is false for days with no aggregation row — those are plotted as 0
      // so the line stays continuous, but styled distinctly (dashed/hollow) below so a
      // no-data day is never confused with a genuine 0% CVR day.
      const hasData = allDates.map(date => dateMap.get(date) != null);
      const data = allDates.map((date, i) => {
        const raw = dateMap.get(date) ?? null;
        return hasData[i] ? (raw as number) * 100 : 0;
      });

      return {
        label: variant?.variantName ?? ts.experienceId,
        data,
        hasData,
        borderColor: color,
        backgroundColor: color,
        borderWidth: 2,
        pointRadius: 3,
        pointBackgroundColor: (ctx: { dataIndex: number }) =>
          hasData[ctx.dataIndex] ? color : '#fff',
        pointBorderColor: color,
        segment: {
          borderDash: (ctx: { p0DataIndex: number; p1DataIndex: number }) =>
            !hasData[ctx.p0DataIndex] || !hasData[ctx.p1DataIndex] ? [4, 4] : undefined,
        },
        tension: 0.2,
      };
    });

    return { labels: allDates, datasets };
  }, [analytics, allDates]);

  const lineChartOptions = useMemo<ChartOptions<'line'>>(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          ticks: {
            autoSkip: true,
            maxRotation: 0,
            maxTicksLimit: 10,
            callback: value => {
              const date = allDates[Number(value)];
              return date ? format(new Date(date), 'MMM d') : '';
            },
          },
        },
        y: {
          ticks: {
            callback: (value: number | string) => {
              const n = Number(value);
              return `${n.toFixed(2)}%`;
            },
          },
        },
      },
      plugins: {
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: items => {
              const date = allDates[items[0]?.dataIndex];
              return date ? format(new Date(date), 'MMM d, yyyy') : '';
            },
            label: ctx => {
              const hasData = (ctx.dataset as unknown as { hasData: boolean[] }).hasData;
              if (!hasData[ctx.dataIndex]) return `${ctx.dataset.label}: No data`;
              const n = Number(ctx.parsed.y);
              return `${ctx.dataset.label}: ${n.toFixed(2)}%`;
            },
          },
        },
      },
    };
  }, [allDates]);

  return (
    <div className='syte-ab-test-dashboard'>
      {/* Header */}
      <div className='dash-header'>
        <span
          role='button'
          tabIndex={0}
          className='dash-back-btn'
          onKeyDown={ev => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              onBack();
            }
          }}
        >
          <Icon name={AvailableIcons.Back} onClick={onBack} />
        </span>
        <div className='dash-header-title'>
          <Typography type={TypographyType.Heading} variant={TypographyVariant.ExtraSmallBold}>
            {experiment.name}
          </Typography>
          <span className={`dash-status-pill experiment-status-color-${status}`}>{status}</span>
        </div>
        <div className='dash-header-actions'>
          {status === ExperimentStatus.Running && applyGate && (
            <Button variant='primary' onClick={() => setShowApplyDialog(true)}>
              Apply winner to shop
            </Button>
          )}
          {status === ExperimentStatus.Running && (
            <Button
              variant='secondary'
              disabled={isStopping}
              loading={isStopping}
              onClick={() => setShowStopDialog(true)}
            >
              {applyGate ? 'End without applying' : 'Stop test'}
            </Button>
          )}
          <Button variant='secondary' onClick={onViewConfig}>
            View config
          </Button>
        </div>
      </div>

      {loading && (
        <div className='syte-ab-test-dashboard-loading' aria-live='polite'>
          Loading analytics…
        </div>
      )}

      {error && !loading && (
        <div className='syte-ab-test-dashboard-error' role='alert'>
          {error}
        </div>
      )}

      {!loading && !error && analytics && (
        <>
          {/* Hero Verdict */}
          {heroVerdict && (
            <div className='hero-verdict'>
              <span className='hero-verdict-icon'>{heroVerdict.icon}</span>
              <div className='hero-verdict-text'>
                <Typography
                  type={TypographyType.Heading}
                  variant={TypographyVariant.ExtraSmallBold}
                >
                  {heroVerdict.title}
                </Typography>
                <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                  {heroVerdict.description}
                </Typography>
              </div>
            </div>
          )}

          {/* Metric Trend Chart */}
          {lineChartData && (
            <div className='metric-trend-chart'>
              <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
                User CVR over time
              </Typography>
              <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                Daily, since experiment start. Watch for the lift to stabilize before stopping.
              </Typography>
              <div className='metric-trend-chart-canvas'>
                <GenericChart type='line' data={lineChartData} options={lineChartOptions} />
              </div>
            </div>
          )}

          {/* Variant Performance Table */}
          <div className='variants-table'>
            <div className='variants-table-title'>
              <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
                Variant performance
              </Typography>
              <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                Lift is computed against the control variant on the primary metric.
              </Typography>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Variant</th>
                  <th>Users</th>
                  <th>Transactions</th>
                  <th>
                    {primaryMetricLabel} <span className='primary-badge'>Primary</span>
                  </th>
                  <th>Lift (CVR)</th>
                </tr>
              </thead>
              <tbody>
                {(analytics.variants ?? []).map(v => {
                  return (
                    <tr key={v.experienceId} className={v.isWinner ? 'winner-row' : ''}>
                      <td>
                        {v.variantName}
                        {v.variantType === 'control' && (
                          <span className='control-badge'>CONTROL</span>
                        )}
                        {v.isWinner && (
                          <span className='trophy-icon'>
                            {' '}
                            <span aria-hidden='true'>🏆</span>
                            <span className='sr-only'>Winner</span>
                          </span>
                        )}
                      </td>
                      <td>{v.users.toLocaleString()}</td>
                      <td>{v.transactions.toLocaleString()}</td>
                      <td>{formatPercent(v.userCvr * 100)}</td>
                      <td>
                        <LiftCell
                          canShowLift={v.variantType !== 'control'}
                          isCollectingData={analytics.state === 'collecting_data'}
                          liftValue={v.lift.cvr}
                          formatLift={formatLiftSigned}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(analytics.variants?.length ?? 0) > 1 && (
              <details className='secondary-metrics'>
                <summary>Show secondary metrics (AOV, ARPU, Revenue)</summary>
                {(() => {
                  const sourceCurrency = findSourceCurrency(analytics.variants);
                  return (
                    sourceCurrency && (
                      <p className='secondary-metrics-currency-note'>
                        Converted from {sourceCurrency}
                      </p>
                    )
                  );
                })()}
                <table>
                  <thead>
                    <tr>
                      <th>Variant</th>
                      <th>AOV</th>
                      <th>ARPU</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics.variants ?? []).map(v => (
                      <tr key={v.experienceId}>
                        <td>{v.variantName}</td>
                        <td>{formatCurrency(v.aov)}</td>
                        <td>{formatCurrency(v.arpu)}</td>
                        <td>{formatCurrency(v.revenue, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>

          {/* Device Breakdown */}
          {(analytics.deviceBreakdown?.length ?? 0) > 0 && (
            <div className='device-breakdown'>
              <div className='device-breakdown-title'>
                <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
                  Breakdown by device
                </Typography>
                <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                  User CVR · variants are flat across all devices
                </Typography>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Users</th>
                    {analytics.deviceBreakdown[0]?.variants.map(v => (
                      <th key={v.variantName}>{v.variantName} CVR</th>
                    ))}
                    <th>Lift (CVR)</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics.deviceBreakdown ?? []).map(row => {
                    const control = row.variants.find(v => v.variantType === 'control');
                    const test = row.variants.find(v => v.variantType !== 'control');
                    const totalUsers = row.variants.reduce((s, v) => s + v.users, 0);
                    return (
                      <tr key={row.deviceType}>
                        <td>
                          {DEVICE_ICONS[row.deviceType.toLowerCase()] ?? '💻'}{' '}
                          {row.deviceType.charAt(0).toUpperCase() + row.deviceType.slice(1)}
                        </td>
                        <td>{totalUsers.toLocaleString()}</td>
                        {row.variants.map(v => (
                          <td key={v.variantName}>{formatPercent(v.userCvr * 100)}</td>
                        ))}
                        <td>
                          <LiftCell
                            canShowLift={Boolean(control && test)}
                            isCollectingData={analytics.state === 'collecting_data'}
                            liftValue={test?.lift.cvr ?? 0}
                            formatLift={formatLiftSigned}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* What Next Card — nothing to roll out when Control itself is the winner */}
          {analytics.state === 'ended_winner' && winnerVariantName && !winnerVariantIsControl && (
            <div className='what-next-card'>
              <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
                What to do next
              </Typography>
              <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                {`To roll out ${winnerVariantName}: update Ranking and Sorting or Merchandising Rules with the winning configuration.`}
              </Typography>
              <Button variant='primary' onClick={onViewConfig}>
                View config
              </Button>
            </div>
          )}
        </>
      )}

      {!loading && !error && !analytics && (
        <div className='syte-ab-test-dashboard-empty'>No analytics data available.</div>
      )}

      {showStopDialog && (
        <StopExperimentModal
          onConfirm={onStopExperiment}
          onCancel={() => setShowStopDialog(false)}
        />
      )}

      {showApplyDialog && applyGate && (
        <ApplyWinnerModal
          shopId={shopId}
          experimentId={experiment.id}
          winningVariantId={applyGate.variantId}
          onApplied={onApplied}
          onCancel={() => setShowApplyDialog(false)}
        />
      )}
    </div>
  );
};
